import moment from 'moment';

/**
 * Calculates user's current age from dateOfBirth
 * @param {object} dateOfBirth - Object with day, month, year
 * @returns {number|null} - Current age in years or null if dateOfBirth is invalid
 */
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth || !dateOfBirth.year || !dateOfBirth.month || !dateOfBirth.day) {
    return null;
  }

  const today = new Date();
  const birthDate = new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day);
  
  if (isNaN(birthDate.getTime())) {
    return null;
  }

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Calculates years since driving license issue date
 * @param {string} drivingLicenseIssue - Date string (format that moment can parse)
 * @returns {number|null} - Years since issue date or null if invalid
 */
const calculateYearsSinceDrivingLicenseIssue = (drivingLicenseIssue) => {
  if (!drivingLicenseIssue) {
    return null;
  }

  const issueDate = moment(drivingLicenseIssue);
  if (!issueDate.isValid()) {
    return null;
  }

  const currentDate = moment();
  const yearsSinceIssue = currentDate.diff(issueDate, 'years', true);
  
  return yearsSinceIssue >= 0 ? yearsSinceIssue : null;
};

/**
 * Maps singpassResult.description to user-friendly decline reason messages
 * @param {string} description - The description from singpassResult.description
 * @param {object} user - User object to check for guestDeclineReason in metadata and calculate age
 * @returns {object|null} - Object with translationKey and values, or null if should not be shown
 */
export const getDeclineReasonMessage = (description, user) => {
  // If guestDeclineReason exists in metadata, don't show decline reason
  if (user && user.attributes && user.attributes.profile && user.attributes.profile.metadata) {
    const guestDeclineReason = user.attributes.profile.metadata.guestDeclineReason;
    if (guestDeclineReason) {
      return null;
    }
  }

  if (!description) {
    return null;
  }

  // Get dateOfBirth for age calculations
  const dateOfBirth = user && user.attributes && user.attributes.profile && user.attributes.profile.protectedData
    ? user.attributes.profile.protectedData.dateOfBirth
    : null;

  // Get drivingLicense for driving license issue date calculations
  const drivingLicense = user && user.attributes && user.attributes.profile && user.attributes.profile.protectedData
    ? user.attributes.profile.protectedData.drivingLicense
    : null;
  const drivingLicenseIssue = drivingLicense && drivingLicense.drivingLicenseIssue
    ? drivingLicense.drivingLicenseIssue
    : null;

  // Mapping table from requirements
  const declineReasonMap = {
    'An account with the same NRIC/ FIN already exists': {
      translationKey: 'identityVerification.declineReason.duplicateNric',
      values: {}
    },
    'age is less than 21 years old': {
      translationKey: 'identityVerification.declineReason.ageLessThan21',
      values: (() => {
        const age = calculateAge(dateOfBirth);
        const requiredAge = 21;
        const yearsNeeded = age !== null ? Math.max(1, requiredAge - age) : 1;
        return { years: yearsNeeded };
      })()
    },
    'drivingLicenseIssue date less than 2 years': {
      translationKey: 'identityVerification.declineReason.dlLessThan2Years',
      values: (() => {
        const yearsSinceIssue = calculateYearsSinceDrivingLicenseIssue(drivingLicenseIssue);
        const requiredYears = 2;
        const yearsNeeded = yearsSinceIssue !== null ? Math.max(1, Math.ceil(requiredYears - yearsSinceIssue)) : 2;
        return { years: yearsNeeded };
      })()
    },
    'drivingLicenseIssue date less than 1 years': {
      translationKey: 'identityVerification.declineReason.dlLessThan1Year',
      values: (() => {
        const yearsSinceIssue = calculateYearsSinceDrivingLicenseIssue(drivingLicenseIssue);
        const requiredYears = 1;
        const yearsNeeded = yearsSinceIssue !== null ? Math.max(1, Math.ceil(requiredYears - yearsSinceIssue)) : 1;
        return { years: yearsNeeded };
      })()
    },
    'driving license issue date less the one year': {
      translationKey: 'identityVerification.declineReason.dlLessThan1Year',
      values: (() => {
        const yearsSinceIssue = calculateYearsSinceDrivingLicenseIssue(drivingLicenseIssue);
        const requiredYears = 1;
        const yearsNeeded = yearsSinceIssue !== null ? Math.max(1, Math.ceil(requiredYears - yearsSinceIssue)) : 1;
        return { years: yearsNeeded };
      })()
    },
    'age is less than 30 years old': {
      translationKey: 'identityVerification.declineReason.ageLessThan30',
      values: (() => {
        const age = calculateAge(dateOfBirth);
        const requiredAge = 30;
        const yearsNeeded = age !== null ? Math.max(1, requiredAge - age) : 1;
        return { years: yearsNeeded };
      })()
    },
    'Duplicate NRIC - Previous linked account is banned': {
      translationKey: 'identityVerification.declineReason.duplicateNricBanned',
      values: {}
    },
    'Duplicate NRIC - Previous linked account in declined state. Needs review': null, // Don't show
    'Driving License validity code is not valid': null, // Don't show
    'Approved and accepted': null, // Don't show
    'license is not found, onfido upload needed': null, // Don't show
    'User residency is not allowed for Singpass oboarding': null, // Don't show
    'driving license pending': null, // Don't show
    'driving license not found': null, // Don't show
    'license is valid and pending for approval': null, // Don't show
  };

  return declineReasonMap[description] || null;
};

/**
 * Gets the decline reason description from user's singpassResult
 * @param {object} user - User object
 * @returns {string|null} - Decline reason description or null
 */
export const getDeclineReasonFromUser = (user) => {
  if (!user || !user.attributes || !user.attributes.profile || !user.attributes.profile.protectedData) {
    return null;
  }

  const singpassResult = user.attributes.profile.protectedData.singpassResult;
  if (!singpassResult || !singpassResult.description) {
    return null;
  }

  return singpassResult.description;
};

